import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import { bdCell, columnOf, labelOf } from './bd-defs.js';
import { tally, tabToneOf, toneOf } from './bd-presentation.js';
import type { BdArtifactDef, BdRow } from '../../../lib/api/basic-design.js';

export interface RiskListViewProps {
  /** リスク一覧の定義（列の見出し・種類・状態の表示名） */
  def: BdArtifactDef;
  /** 保存済みの行 */
  rows: readonly BdRow[];
}

/**
 * リスク一覧を読む。行を状態で色分けしたカードで並べ、状態ごとの件数で絞れるようにする。
 *
 * 読み手が最初に知りたいのは「手を打っていないリスクがいくつ残っているか」なので、
 * 状態の件数を上に出し、未対応を赤で拾えるようにする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business リスク一覧を、状態で色分けしたカードと状態ごとの件数の絞り込みで読む
 * @graph-connects tally [calls] 状態ごとの件数
 */
export function RiskListView({ def, rows }: RiskListViewProps) {
  const { t } = useTranslation();
  // 空文字は絞らない（すべて）
  const [state, setState] = useState('');
  const counts = tally(
    rows.map((r) => bdCell(r, 'state')),
    columnOf(def, 'state')?.choices,
    t('bd.filter.unselected'),
  );
  const shown = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => state === '' || bdCell(row, 'state') === state);
  const label = (key: string): string => columnOf(def, key)?.label ?? key;
  const choice = (row: BdRow, key: string): string => labelOf(columnOf(def, key), bdCell(row, key));

  return (
    <div className="space-y-3">
      <TabGroup ariaLabel={t('bd.risk.filterAria')}>
        <TabButton
          active={state === ''}
          label={t('bd.filter.all')}
          count={rows.length}
          onClick={() => {
            setState('');
          }}
        />
        {counts.map((c) => (
          <TabButton
            key={c.value}
            active={state === c.value}
            label={c.label}
            count={c.count}
            tone={tabToneOf('state', c.value)}
            disabled={c.count === 0}
            onClick={() => {
              setState(c.value);
            }}
          />
        ))}
      </TabGroup>

      <ol className="space-y-3" aria-label={t('bd.view.rowsAria', { title: def.title })}>
        {shown.map(({ row, index }) => (
          // 同じ中身の行が並ぶことがあるので、保存済みの並びの位置で鍵を作る
          <li key={index}>
            <Card padding="sm">
              <CardContent className="mt-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
                    #{index + 1}
                  </span>
                  <Badge>{choice(row, 'kind')}</Badge>
                  <h3 className="min-w-0 flex-1 text-sm font-semibold text-[var(--color-text-primary)]">
                    {bdCell(row, 'what')}
                  </h3>
                  <Badge variant={toneOf('state', bdCell(row, 'state'))}>
                    {choice(row, 'state')}
                  </Badge>
                </div>
                <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 border-t border-[var(--color-border)] pt-3 text-sm md:grid-cols-[5rem_1fr]">
                  <dt className="text-xs text-[var(--color-text-muted)]">{label('mitigation')}</dt>
                  <dd className="whitespace-pre-wrap text-[var(--color-text-primary)]">
                    {bdCell(row, 'mitigation')}
                  </dd>
                  <dt className="text-xs text-[var(--color-text-muted)]">{label('rollback')}</dt>
                  <dd className="whitespace-pre-wrap text-[var(--color-text-primary)]">
                    {bdCell(row, 'rollback')}
                  </dd>
                </dl>
              </CardContent>
            </Card>
          </li>
        ))}
      </ol>
    </div>
  );
}
